<template>
  <line-chart
    :title="displayWithObjectives ? 'CA vs Objectif' : 'CA'"
    :loading="loading"
    :colors="colors"
    :series="series"
    :yAxis="yAxis"
    :xAxis="xAxis"
    :dags="[
      'partner_get_and_store_daily_dag',
      'partner_get_and_store_daily_ifempty_dag',
    ]"
  ></line-chart>
</template>

<script>
import { cacheTime } from "@axios";
import moment from "moment/moment";

import useAxios from "@/hooks/useAxios";
import { runParallelAsyncs } from "@/utils/async";
import { colors } from "@/utils/constants";
import { getDate, getDaysArray, monthRange } from "@/utils/dates";
import { formatNumber } from "@/utils/formatting";
import LineChart from "@/components/common/charts/LineChart";

export default {
  name: "CaObjectivesChart",
  components: {
    LineChart,
  },
  data() {
    return {
      loading: false,
      displayWithObjectives: false,
      exportSuiviGeneral: null,
      requiredViewIdsForObjectives: [],
      series: [
        {
          name: "CA",
          data: [],
        },
      ],
      colors: [colors.ca, colors.objective], // "#2da0ed", "#00d4bd", "#9C27B0",
      yAxis: [
        {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: (val) => formatNumber(val),
          },
        },
      ],
      xAxis: {
        categories: [],
      },
    };
  },
  setup() {
    const { axiosGet } = useAxios();

    return {
      axiosGet,
    };
  },
  methods: {
    async getData() {
      if (
        this.applyFilters &&
        !this.loading &&
        this.month &&
        this.month.length > 5
      ) {
        const queryParams = {
          month: this.month,
          ...(this.sites && this.sites.length > 0
            ? { sites: this.sites.join(",") }
            : {}),
          ...(this.partnerViews && this.partnerViews.length > 0
            ? { partner_views: this.partnerViews.join(",") }
            : {}),
        };
        const range = monthRange(this.month);
        const categories = getDaysArray(range[0], range[1]);
        const mainData = {};
        const yesterday = moment(getDate(-1));
        for (const x of categories) {
          const defaultValue = moment(new Date(x)) >= yesterday ? null : 0;
          mainData[x] = {
            ca: defaultValue,
            objRevenue: defaultValue,
          };
        }

        this.loading = true;
        const {
          detailedRevenueResponse: { data: detailedRevenueData },
          detailedAudienceResponse: { data: detailedAudienceData },
          totalResponse: { data: totalData },
        } = await runParallelAsyncs({
          detailedRevenueResponse: this.axiosGet(
            "/programmatic/detailed-revenue-monthly-report",
            queryParams,
            {
              cache: {
                maxAge: cacheTime,
              },
            }
          ),
          detailedAudienceResponse: this.axiosGet(
            "/programmatic/detailed-audience-monthly-report",
            queryParams,
            {
              cache: {
                maxAge: cacheTime,
              },
            }
          ),
          totalResponse: this.axiosGet(
            "/programmatic/total-monthly-report",
            queryParams,
            {
              cache: {
                maxAge: cacheTime,
              },
            }
          ),
        });

        this.xAxis = {
          ...this.xAxis,
          categories: categories,
        };

        detailedRevenueData.map((data) => {
          mainData[data["date"]]["ca"] = data["ca"];
        });
        detailedAudienceData.map((data) => {
          mainData[data["date"]]["sessions"] = data["sessions"];
        });

        this.series = [
          {
            name: "CA",
            data: categories.map((category) => mainData[category]["ca"]),
          },
        ];

        if (this.displayWithObjectives) {
          delete queryParams["partner_views"];
          queryParams["objtype"] = "sessions";
          const { data: objectivesSessionsData } = await this.axiosGet(
            "/objectives/",
            queryParams,
            {
              cache: {
                maxAge: cacheTime,
              },
            }
          );

          const objectiveRevenueDay =
            totalData["current_ca_objective"] / categories.length;

          const objectiveSessionsMonth = objectivesSessionsData["value"];
          const objectiveSessionsDay =
            objectiveSessionsMonth / categories.length;

          const objectiveRPMDay =
            (objectiveRevenueDay / objectiveSessionsDay) * 1000;
          for (const x of categories) {
            mainData[x]["objRevenue"] = objectiveRevenueDay;
            mainData[x]["objSessions"] = objectiveSessionsDay;
            mainData[x]["objRPM"] = objectiveRPMDay;
          }

          this.series.push({
            name: "Objectif",
            data: categories.map(
              (category) => mainData[category]["objRevenue"]
            ),
          });
        }

        this.loading = false;
      }

      // Force clicking on button again to re-run that request :
      // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
      this.$store.dispatch("common/updateApplyFilters", false);
    },
    async setDisplayWithObjectives() {
      if (this.exportSuiviGeneral === null) {
        this.exportSuiviGeneral = this.partnerViewGroupList.find(
          (group) => group.name === "Export Suivi Général"
        );
      }

      if (
        this.exportSuiviGeneral === undefined ||
        this.partnerViewGroups.length !== 1 ||
        this.partnerViewGroups[0] !== this.exportSuiviGeneral.id
      ) {
        this.displayWithObjectives = false;

        return;
      }

      if (this.requiredViewIdsForObjectives.length === 0) {
        const { data } = await this.axiosGet(
          "/partner-view-groups/partner-views",
          { group_ids: this.exportSuiviGeneral.id }
        );
        this.requiredViewIdsForObjectives = data.map(
          (x) => x["partner_view_id"]
        );
      }

      this.displayWithObjectives =
        this.requiredViewIdsForObjectives.every((id) =>
          this.partnerViews.includes(id)
        ) &&
        this.partnerViews.every((id) =>
          this.requiredViewIdsForObjectives.includes(id)
        );
    },
  },
  computed: {
    applyFilters() {
      return this.$store.getters["common/getApplyFilters"];
    },
    sites() {
      return this.$store.getters["premium/getSites"];
    },
    month() {
      return this.$store.getters["premium/getMonthDate"];
    },
    partnerViews() {
      return this.$store.getters["premium/getPartnerViews"];
    },
    partnerViewGroups() {
      return this.$store.getters["premium/getPartnerViewGroups"];
    },
    partnerViewGroupList() {
      return this.$store.getters["common/getPartnerViewGroupList"];
    },
  },
  watch: {
    applyFilters() {
      this.getData();
    },
    partnerViews() {
      this.setDisplayWithObjectives();
    },
    partnerViewGroups() {
      this.setDisplayWithObjectives();
    },
  },
};
</script>
